"use client";

import { useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import { ArrowLeft } from "lucide-react";
import { isAndroid } from "@/lib/utils";
import { useI18n } from "@/components/providers/i18n-provider";
import { useTranslations } from "@/hooks/use-translations";
import { useIsNativeShell } from "@/app/(site)/native-shell-context";

interface LegalPageLayoutProps {
  title: string;
  children: React.ReactNode;
  /** When true, shows the translation disclaimer for non-English locales */
  showTranslationDisclaimer?: boolean;
  /** Short line under the title on the website, e.g. what the document is for. */
  intro?: string;
  /**
   * e.g. "9 September 2026". Rendered by the layout so it sits correctly in both chromes.
   *
   * It must be a WRITTEN date, naming the day the words on the page last changed, and it must be
   * moved by hand in the same commit that changes them. Never compute it. The two deletion pages
   * passed `new Date()` here, so they told every visitor the policy had been updated this month,
   * every month, without anyone having touched it since May: a date that is never true rather than
   * one that is merely old. A layout or typography change is not a change to the document and must
   * not move it.
   */
  lastUpdated?: string;
  /** Overrides the "Legal documents" kicker on the website. Support is not a legal document. */
  eyebrow?: string;
}

/**
 * The wrapper for Terms, Privacy, Cookies, Child Safety, Support and the two deletion pages.
 *
 * These are the only pages that have to be two things at once. Inside the app they are a plain
 * screen reached from settings: a back button, no branding, and on Android a real scroll
 * container, because without one the WebView will not scroll a long document reliably. On the
 * website they are pages of the website, and until 2026-08-24 they were not: they rendered that
 * same bare app screen, hard against the left edge with no logo, no navigation and no footer,
 * which looked like a different company's site sitting on the same domain.
 *
 * The app version below is unchanged, deliberately. Which one renders is decided on the server
 * (see app/(site)/layout.tsx) so there is no flash of the wrong chrome.
 */
export function LegalPageLayout({
  title,
  children,
  showTranslationDisclaimer = true,
  intro,
  lastUpdated,
  eyebrow,
}: LegalPageLayoutProps) {
  const router = useRouter();
  const isNativeShell = useIsNativeShell();
  const [useAndroidScrollFix, setUseAndroidScrollFix] = useState(false);
  const { locale } = useI18n();
  const { t } = useTranslations("legal");
  const isNonEnglish = locale && locale !== "en" && !locale.startsWith("en-");

  useEffect(() => {
    setUseAndroidScrollFix(isAndroid());
  }, []);

  const disclaimer = showTranslationDisclaimer && isNonEnglish && (
    <div
      data-legal-disclaimer
      className="rounded-2xl border border-amber-200 bg-amber-50/60 p-4"
    >
      <p className="text-sm text-amber-900">
        <strong>{t("translationDisclaimer")}</strong>
      </p>
    </div>
  );

  /* ---------------------------------------------------------------- website ------ */
  if (!isNativeShell) {
    return (
      <div data-site-doc className="relative">
        {/* The same wash the marketing pages open on, so a visitor arriving from the footer
            does not feel they have left the site. */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-x-0 top-0 h-[320px]"
          style={{
            background:
              "radial-gradient(60% 100% at 30% 0%, rgba(33,122,246,0.10) 0%, rgba(33,122,246,0.04) 45%, rgba(250,250,250,0) 78%)",
          }}
        />
        <div className="gs-page relative py-14 md:py-20">
          <header className="max-w-[58rem]">
            <p className="gs-eyebrow text-primary">{eyebrow ?? t("legalDocuments")}</p>
            <h1 className="gs-headline mt-3 text-balance text-neutral-900">{title}</h1>
            {intro && <p className="gs-lede mt-4 gs-measure text-neutral-600">{intro}</p>}
            {lastUpdated && (
              <p className="mt-5 text-sm text-neutral-500">Last updated: {lastUpdated}</p>
            )}
          </header>

          {disclaimer && <div className="mt-8 max-w-[58rem]">{disclaimer}</div>}

          <div data-doc-body className="mt-10 max-w-[54rem] border-t border-neutral-200 pt-10">
            {children}
          </div>
        </div>
      </div>
    );
  }

  /* ------------------------------------------------------------------- the app ---- */
  const handleBack = () => {
    if (typeof window !== "undefined") {
      const hasReferrer = document.referrer && document.referrer !== window.location.href;
      const hasHistory = window.history.length > 1;

      if (hasReferrer || hasHistory) {
        router.back();
      } else {
        router.push("/");
      }
    } else {
      router.push("/");
    }
  };

  return (
    <div
      className={
        useAndroidScrollFix
          ? "flex flex-col h-viewport-fixed min-h-viewport-fixed bg-white"
          : "min-h-screen bg-white"
      }
    >
      {/* Header with back button - flex-shrink-0 on Android so it stays fixed */}
      <header
        className={`z-40 border-b border-neutral-200 bg-white/95 backdrop-blur-sm ${
          useAndroidScrollFix ? "flex-shrink-0" : "sticky top-0"
        }`}
        style={{
          paddingTop: "env(safe-area-inset-top, 0px)",
        }}
      >
        <div className="flex h-16 w-full items-center gap-4 px-6 py-2 md:h-20 md:px-10 md:py-3 lg:px-12">
          <button
            onClick={handleBack}
            className="flex items-center gap-2 text-sm font-medium text-neutral-600 hover:text-neutral-900 transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
            aria-label="Go back"
          >
            <ArrowLeft className="h-5 w-5" strokeWidth={2} />
            <span className="hidden sm:inline">Back</span>
          </button>
          <h1 className="text-lg font-semibold text-neutral-900 md:text-xl flex-1">
            {title}
          </h1>
        </div>
      </header>

      {showTranslationDisclaimer && isNonEnglish && (
        <div className="mx-6 mt-4 mb-2 md:mx-10 lg:mx-12">{disclaimer}</div>
      )}
      {lastUpdated && (
        <p className="px-6 pt-4 text-sm text-neutral-500 md:px-10 lg:px-12">
          Last updated: {lastUpdated}
        </p>
      )}

      {/* Content - scrollable region on Android (fixes real-device touch scroll) */}
      <div
        data-legal-content
        {...(useAndroidScrollFix && { "data-app-scroll-container": "" })}
        className={
          useAndroidScrollFix
            ? "flex-1 min-h-0 overflow-y-auto overflow-x-hidden w-full px-6 py-6 md:px-10 md:py-8 lg:px-12"
            : "w-full px-6 py-6 md:px-10 md:py-8 lg:px-12"
        }
        style={{
          paddingBottom: "calc(1.5rem + env(safe-area-inset-bottom, 0px))",
          ...(useAndroidScrollFix && {
            WebkitOverflowScrolling: "touch",
            touchAction: "pan-y",
          }),
        }}
      >
        {children}
      </div>
    </div>
  );
}
